import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  AUFGABEN_GROESSEN,
  entstanden,
  GROESSE_LABELS,
  lesbarerText,
  nurVorraete,
  TASK_PRIORITIES,
  tagKompakt,
  ZEITFENSTER,
  ZEITFENSTER_LABELS,
  type Aufgabengroesse,
  type TaskPriority,
  type TaskSource,
  type PersonRole,
  type Zeitfenster,
  zaehltAlsEntwickler,
} from '@netcon-work/shared'
import { AufgabenVerweis } from '../components/AufgabeKontext'
import { useMerker } from '../lib/merker'
import { AnfragenListe } from '../components/Anfragen'
import { Topbar } from '../components/Topbar'
import { AufgabenWeltMarke } from '../components/WorkspaceMarke'
import { Button, Card, CardPad, SectionTitle, SourceBadge } from '../components/ui/primitives'
import {
  eingangKanaeleQuery,
  workspacesQuery,
  eingangListeQuery,
  massenaktionenQuery,
  poolsAllerWeltenQuery,
  sitzungQuery,
  vorschlagsStufenQuery,
} from '../lib/queries'
import { trpc } from '../lib/trpc'
import { usePersonenDieserWelt } from '../lib/welt'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * Das Ziel des Hinweises bei leerer Suche – als `string` und nicht als Literal. Die Routen
 * entstehen in `router.tsx` aus einer Liste und sind nicht typregistriert; ein Literal
 * `"/alle"` lehnt `Link` ab. Dieselbe Stelle wie `ZUM_EINGANG` in `Trichter.tsx`.
 */
const ZU_ALLEN: string = '/alle'

/**
 * **Der Eingang** (Phase 6, Konzept Kap. 4.1, Mockup `v-eingang`).
 *
 * ## Was hier fehlt und warum das so bleibt, bis es echt ist
 *
 * Das Mockup zeigt je Karte einen KI-Vorschlag mit Konfidenz („92 %") und einen
 * Ähnlichkeitshinweis („ähnlich #388, keine Dublette"). Beides braucht einen
 * Modellzugang, und den gibt es bewusst noch nicht – die Schlüssel sollen später über die
 * Oberfläche eingegeben werden.
 *
 * **Der Platz dafür bleibt frei, statt mit einer Attrappe gefüllt zu werden.** Eine
 * erfundene Konfidenz ist schlimmer als keine: sie sieht aus wie eine Messung, und wer
 * ihr einmal geglaubt hat, glaubt der echten später nicht mehr.
 *
 * ## Was die Ansicht dafür kann
 *
 * Einordnen – **für viele Aufgaben auf einmal**. Die Messung vom 02.08.2026 sagt, warum
 * das die wichtigere Hälfte ist: der Zulauf ist klein (3–6 pro Tag), der Rückstau nicht.
 * 139 von 150 offenen Aufgaben liegen in keinem Pool, 112 haben keine Priorität. Gegen
 * einen Stapel von 67 ist eine Ansicht machtlos, die nur einzeln kann.
 *
 * ## Warum „Eingang" hier etwas anderes heißt
 *
 * Alle 67 Aufgaben stammen aus **GitLab**, keine aus NetCon. Ein Ticket trägt dort immer
 * einen Status, ein GitLab-Issue **ohne `Status::`-Label** nicht – und landet deshalb
 * hier. Der Eingang ist nicht „das Neue", sondern „das Unetikettierte".
 */
/**
 * Die vier Sortierungen – Beschriftung und Erklärung an **einer** Stelle. Vorher stand die
 * Erklärung als fester Text unter der Liste und passte nach dem ersten Umschalten nicht mehr.
 */
const SORTIER_LABEL = {
  alter: 'älteste zuerst',
  neueste: 'neueste zuerst',
  prioritaet: 'Priorität',
  vorschlaege: 'KI-Vorschläge zuerst',
} as const

const SORTIER_HINWEIS: Record<keyof typeof SORTIER_LABEL, string> = {
  alter: 'älteste zuerst – was hier am längsten liegt, ist am ehesten vergessen',
  neueste: 'neueste zuerst – was gerade zugelaufen ist, steht oben',
  prioritaet: 'nach Priorität – A vor B vor C, ohne Angabe zuletzt',
  vorschlaege: 'KI-Vorschläge zuerst – was auf eine Entscheidung wartet, steht oben',
}

export function Eingang() {
  const queryClient = useQueryClient()
  /**
   * **Sortierung ist eine Frage, keine Voreinstellung** (Nils, 18.08.2026: *„Ich muss im
   * Eingang auch die neuesten sehen, also selber sortieren können."*). „Älteste zuerst"
   * bleibt der Standard – was am längsten liegt, ist am ehesten vergessen –, aber wer
   * gerade etwas gemeldet hat, sucht es oben.
   *
   * **Die Wahl gilt für alle Zeilen** (18.08.2026). Bis dahin wurden Aufgaben mit offenem
   * KI-Vorschlag in jeder Sortierung nach oben gezogen; „neueste zuerst" hieß dann in
   * Wahrheit „erst die blauen, dann die neuesten". Das Hochziehen ist jetzt eine eigene
   * Sortierung neben den anderen.
   */
  /**
   * **Neueste zuerst ist die Voreinstellung** (Nils, 24.08.2026: *„Die Sortierung sollte
   * standardmäßig ‚neueste zuerst' sein."*).
   *
   * Der Eingang ist der Ort, an dem man **Zugelaufenes** einordnet, und zugelaufen ist zuerst
   * das Neue. „Älteste zuerst" beantwortet eine andere, seltenere Frage – *was liegt hier schon
   * zu lange* –, und dafür gibt es den Knopf daneben und den Trichter.
   */
  const [sortierung, setSortierung] = useState<'alter' | 'neueste' | 'prioritaet' | 'vorschlaege'>(
    'neueste',
  )
  /** Zugeklappt bleibt zugeklappt – über Sitzungen hinweg (`useMerker`). */
  const [werkzeugeOffen, setWerkzeugeOffen] = useMerker('eingang-werkzeuge', 'auf', [
    'auf',
    'zu',
  ] as const)
  /**
   * **Der Eingang ist vom Umschalter losgelöst** (01.09.2026,
   * [E-296](../../../docs/ENTSCHEIDUNGEN.md#e-296)).
   *
   * Nils: *„Der Eingang sollte losgelöst vom Workspace bzw. der Auswahl sein. […] der Eingang
   * soll Workspaceübergreifend (im Rahmen meiner Rechte) anzeigen!"*
   *
   * Hier stand bis dahin ein Schalter „Nur diese Arbeitswelt / Alle Arbeitswelten"
   * ([E-285](../../../docs/ENTSCHEIDUNGEN.md#e-285)) mit „nur diese" als Vorgabe. Er ist weg,
   * und mit ihm der gemerkte Stand: **wer einsortiert, sortiert alles ein, was ihm gehört.**
   * Ein Schalter, dessen richtige Stellung immer dieselbe ist, ist nur eine Gelegenheit, ihn
   * falsch stehen zu lassen.
   *
   * **Keine Rechtefrage.** Was überhaupt herauskommt, entscheidet `aufgabenSichtbarkeit` auf dem
   * Server – Mitgliedschaft und Rolle. Der Client verengt nichts mehr und öffnet nichts.
   */
  const { data: aufgaben = [], isLoading } = useQuery(eingangListeQuery(sortierung))
  const { data: kanaele = [] } = useQuery(eingangKanaeleQuery)
  const { data: welten } = useQuery(workspacesQuery)
  /**
   * **Nur die Vorräte stehen zur Wahl** (20.08.2026, [E-191](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Der Parkplatz gehört nicht in die Einordnen-Auswahl: „Einordnen" setzt keinen Status, und
   * eine Aufgabe im Regal ohne „Zurückgestellt" wäre halb geparkt. Dafür gibt es den eigenen
   * Knopf daneben – der Server weist den anderen Weg auch ab.
   */
  /**
   * **Der Vorrat aller meiner Arbeitswelten** (01.09.2026,
   * [E-296](../../../docs/ENTSCHEIDUNGEN.md#e-296)).
   *
   * Hier stand `poolsQuery` – der Vorrat der **aktiven** Welt. Das passte, solange die Liste
   * darüber dieselbe Verengung hatte, und wurde in dem Moment falsch, in dem sie es nicht mehr
   * hat: eine Marketing-Aufgabe hätte nur Entwicklungs-Pools zur Wahl gehabt, und wer einen
   * davon nimmt, legt sie in einen Vorrat, den in ihrer Welt niemand sieht. Welche Pools an
   * welcher Zeile stehen dürfen, entscheidet ab hier {@link poolsDerWelt}.
   */
  const { data: allePools = [] } = useQuery(poolsAllerWeltenQuery)
  const pools = useMemo(() => nurVorraete(allePools), [allePools])
  /**
   * **Die Vorräte je Arbeitswelt** – ein Pool gehört zu genau einer, und eine Aufgabe auch.
   * Vorgehalten als Karte, weil jede Zeile und jedes Popup dieselbe Frage stellt.
   */
  const poolsJeWelt = useMemo(() => {
    const karte = new Map<string, typeof pools>()
    for (const p of pools) karte.set(p.workspaceId, [...(karte.get(p.workspaceId) ?? []), p])
    return karte
  }, [pools])
  const poolsDerWelt = (workspaceId: string | null | undefined) =>
    (workspaceId ? poolsJeWelt.get(workspaceId) : undefined) ?? []
  const { data: sitzung } = useQuery(sitzungQuery)
  const rolle = sitzung?.role ?? 'entwickler'

  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set())
  /**
   * **Filter nach Bereich** – seit [F-32](../../../../docs/OFFENE-FRAGEN.md) möglich.
   *
   * Bei 67 Aufgaben aus fünf Projekten ist „alles auf einmal" die falsche Portion: wer
   * `NetConWeb` sortiert, denkt an andere Pools als bei `Services`. Einen Bereich nach dem
   * anderen zu nehmen ist die Art, wie man so einen Stapel wirklich abarbeitet.
   */
  const [bereich, setBereich] = useState('alle')
  /**
   * **Filter nach Kanal** (Nils, 20.08.2026: *„die Eingänge sind aktuell nicht
   * filterbar/klickbar … einfacher, wenn man nur nach NetCon/GitLab/Intranet filtern kann"*).
   *
   * Die Kopfleiste stand bis dahin nur zur Auskunft da, obwohl sie aussah wie eine Reihe
   * Knöpfe. Ein Kanal ist die gröbste sinnvolle Portion: ein GitLab-Issue sortiert man anders
   * als ein NetCon-Ticket, weil dahinter andere Leute und andere Pools stehen.
   */
  const [kanal, setKanal] = useState('alle')
  /**
   * **Suche im Eingang** (Nils, 21.08.2026: *„Wie suche ich denn nach Aufgaben in Eingang?"*).
   *
   * Die Kanal- und Bereichsknöpfe portionieren den Stapel, aber sie finden nichts: wer eine
   * bestimmte Ticketnummer sucht, weil gerade jemand danach fragt, musste die Ansicht wechseln.
   *
   * **Gesucht wird dasselbe wie in „Alle Aufgaben"** – Titel, Kennung, Bearbeiter. Zwei Suchen
   * mit unterschiedlicher Reichweite wären schlimmer als eine: wer hier nichts findet, würde
   * dort auch nicht mehr nachsehen.
   */
  const [suche, setSuche] = useState('')
  /**
   * **Das Detail auf einen Klick** – Nils, 03.08.2026: *„Sonst kann ich den Inhalt nie
   * beurteilen."* Er hat recht: ein Vorschlag „Pool: tickets" ist ohne den Text der Aufgabe
   * nicht zu bewerten, und zwischen zwei Ansichten hin- und herzuspringen macht aus einer
   * Entscheidung eine Recherche.
   */
  const [pool, setPool] = useState<string>('')
  const [prio, setPrio] = useState<TaskPriority | ''>('')

  /** Welche Aufgabe gerade im Bearbeiten-Popup steht. `null` = keins offen. */
  const [bearbeite, setBearbeite] = useState<string | null>(null)

  /**
   * **Die Personen für „Kommen in Frage"** (20.08.2026). Das Popup muss sie korrigieren
   * können wie jedes andere KI-Feld – und dafür braucht es Namen statt der Kennungen, die in
   * `ai_suggestion.vorschlag` stehen. Der Bestand liegt ohnehin im Client (Leitplanke 2), die
   * Abfrage kostet hier nichts.
   */
  /**
   * **Nur wer in der Welt *dieser Aufgabe* ist** (Z-07, 26.08.2026; auf die Aufgabe umgestellt am
   * 01.09.2026, [E-296](../../../docs/ENTSCHEIDUNGEN.md#e-296)).
   *
   * Bis dahin genügte die **aktive** Welt, weil der Eingang an sie gebunden war. Jetzt steht im
   * Popup womöglich eine Aufgabe aus einer anderen – und die Auswahl hätte Leute angeboten, die
   * sie nie zu sehen bekämen: die Zuweisung ginge durch, die Aufgabe verschwände für den
   * Zugewiesenen. Dieselbe Überlegung wie im Aufgabendetail (28.08.2026).
   */
  const { personen } = usePersonenDieserWelt(
    aufgaben.find((a) => a.id === bearbeite)?.workspaceId ?? null,
  )

  /**
   * **Ein Knopf, alle Felder** (Nils, 20.08.2026: *„Ich möchte nicht 4 Stück je Aufgabe
   * haben … dann kann man so einen Eingang auch schnell durcharbeiten."*).
   *
   * Der Server bekommt, was **gelten soll** – ob ein Feld damit als übernommen oder als
   * geändert zählt, rechnet er aus dem Vergleich mit dem Vorschlag aus. Der Client müsste
   * sonst mitführen, was das Modell ursprünglich sagte, und würde es beim nächsten Umbau
   * vergessen.
   */
  const uebernehmen = useMutation({
    mutationFn: (v: {
      taskId: string
      werte: Record<string, string | null>
      felder?: Record<string, string | null>
    }) => trpc.eingang.triageUebernehmen.mutate(v),
    onSuccess: async () => {
      setBearbeite(null)
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await nebenBestandVerwerfen(queryClient)
    },
  })

  /**
   * **Ausdrücklich angestoßen, nicht im Zeitgeber.** Ein Hintergrundjob, der Modellaufrufe
   * erzeugt, ist eine Rechnung, die niemand ausgelöst hat.
   */
  const erzeugen = useMutation({
    mutationFn: (v: { limit: number }) => trpc.ki.vorschlaegeErzeugen.mutate(v),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await queryClient.invalidateQueries({ queryKey: ['ki'] })
    },
  })

  /**
   * **Den Personenvorschlag einer Regel übernehmen** (18.08.2026,
   * [E-128](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Nils, zur Stufe: *„Ja, als Vorschlag."* Der Vorschlag schreibt also nichts – aber ein
   * Vorschlag ohne Knopf ist ein Satz, der jemanden zum Abtippen zwingt. Ein Klick trägt
   * ihn ein, und ab da ist es eine Angabe an der Aufgabe wie jede andere.
   *
   * Geschrieben wird über `tasks.planungSetzen` – dieselbe Prozedur wie im Aufgabendetail.
   * Eine zweite Schreibstelle für dasselbe Feld wäre die zweite Stelle, an der „kommen in
   * Frage" etwas bedeutet.
   */
  const wunschUebernehmen = useMutation({
    mutationFn: (v: { id: string; wunschPersonIds: string[] }) =>
      trpc.tasks.planungSetzen.mutate(v),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['pools'] })
    },
  })

  const einordnen = useMutation({
    mutationFn: (eingabe: { taskIds: string[]; poolIds: string[]; priority?: TaskPriority }) =>
      trpc.eingang.einordnen.mutate(eingabe),
    onSuccess: async () => {
      setGewaehlt(new Set())
      setPool('')
      setPrio('')
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await nebenBestandVerwerfen(queryClient)
    },
  })

  /**
   * **Parken** – der Abwurf für alles, was nur zur Kenntnis da ist (20.08.2026,
   * [E-191](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Nils: *„Die sollen sich nicht gezogen werden können, das dient nur dazu, die erst mal aus
   * dem Eingang rauszubekommen."* Ein Knopf, kein Dialog: wer einen Stapel alter Wünsche
   * durchgeht, entscheidet je Zeile in einer Sekunde – und die Rücknahme steht in „Zuletzt
   * gemacht", falls eine davon falsch war.
   */
  const parken = useMutation({
    mutationFn: (taskIds: string[]) => trpc.eingang.parken.mutate({ taskIds }),
    onSuccess: async () => {
      setGewaehlt(new Set())
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['pools'] })
    },
  })

  function umschalten(id: string) {
    setGewaehlt((alt) => {
      const neu = new Set(alt)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })
  }

  /**
   * **Wie viele Zeilen je Kanal wirklich hier stehen.** Die Zahl kommt aus der geladenen
   * Liste, nicht aus `kanaele.offen` – die zählt Aufgaben im *Status* `eingang`, und das ist
   * seit dem 18.08.2026 nicht mehr dasselbe wie „unverteilt". Eine Karte, die 31 sagt und 64
   * filtert, wäre ein Knopf, der lügt.
   */
  /**
   * **Die Suche greift vor den Knöpfen**, nicht danach. Sonst stünde an einer Kanalkarte „31",
   * während die Suche im ganzen Kanal zwei Zeilen übrig lässt – und die Karten sind hier
   * Filter, keine Verzierung: eine Zahl, die nach dem Klick nicht stimmt, ist ein Knopf, der
   * lügt (siehe `proKanal`).
   */
  const gesucht = useMemo(() => {
    const begriff = suche.trim().toLowerCase()
    if (!begriff) return aufgaben
    return aufgaben.filter(
      (a) =>
        a.title.toLowerCase().includes(begriff) ||
        (a.externalKey?.toLowerCase().includes(begriff) ?? false) ||
        (a.externalId?.toLowerCase().includes(begriff) ?? false) ||
        (a.assigneeName?.toLowerCase().includes(begriff) ?? false),
    )
  }, [aufgaben, suche])

  const proKanal = useMemo(() => {
    const karte = new Map<string, number>()
    for (const a of gesucht) karte.set(a.sourceId, (karte.get(a.sourceId) ?? 0) + 1)
    return karte
  }, [gesucht])

  const nachKanal = useMemo(
    () => (kanal === 'alle' ? gesucht : gesucht.filter((a) => a.sourceId === kanal)),
    [gesucht, kanal],
  )

  /**
   * Die Bereiche zählen **innerhalb des gewählten Kanals**. Sonst stünde an einem Knopf eine
   * Zahl, die nach dem Klick nicht mehr stimmt – und „Services (6)", das 2 Zeilen zeigt, ist
   * schlimmer als kein Zähler.
   */
  const bereiche = useMemo(() => {
    const karte = new Map<string, { label: string; anzahl: number }>()
    for (const a of nachKanal) {
      if (!a.scope) continue
      const da = karte.get(a.scope)
      if (da) da.anzahl++
      else karte.set(a.scope, { label: a.scopeLabel ?? a.scope, anzahl: 1 })
    }
    return [...karte.entries()]
      .map(([wert, { label, anzahl }]) => ({ wert, label, anzahl }))
      .sort((x, y) => y.anzahl - x.anzahl)
  }, [nachKanal])

  const sichtbar = useMemo(
    () => (bereich === 'alle' ? nachKanal : nachKanal.filter((a) => a.scope === bereich)),
    [nachKanal, bereich],
  )

  /**
   * **Kanalwechsel setzt den Bereich zurück.** Ein Bereich gehört meist zu genau einem Kanal;
   * bliebe er stehen, träfe der Wechsel auf eine leere Liste – und die sähe aus wie „hier ist
   * nichts", nicht wie „zwei Filter schließen sich aus".
   */
  function waehleKanal(neu: string) {
    setKanal(neu)
    setBereich('alle')
    setGewaehlt(new Set())
  }

  /**
   * **Tippen hebt die Auswahl auf.** Die Sammelaktion arbeitet auf `gewaehlt`, und darin
   * dürfen keine Zeilen stehen, die man gerade nicht mehr sieht – sonst ordnet ein Klick auf
   * „12 einordnen" Aufgaben ein, die der Suchbegriff längst ausgeblendet hat.
   */
  function waehleSuche(neu: string) {
    setSuche(neu)
    setBereich('alle')
    setGewaehlt(new Set())
  }

  const offeneVorschlaege = sichtbar.filter((a) => a.vorschlagId !== null).length

  const alleGewaehlt = sichtbar.length > 0 && gewaehlt.size === sichtbar.length

  /**
   * **Die Arbeitswelten der gewählten Zeilen** (01.09.2026,
   * [E-296](../../../docs/ENTSCHEIDUNGEN.md#e-296)).
   *
   * Seit der Eingang über alle Welten geht, kann eine Auswahl zwei davon enthalten. Ein Pool
   * gehört aber zu genau einer – „Sofort" aus der Entwicklung an einer Marketing-Aufgabe wäre
   * ein Vorrat, den in ihrer Welt niemand sieht. Der Server weist das ab; hier wird es gar
   * nicht erst angeboten.
   */
  const weltenDerAuswahl = useMemo(() => {
    const gefunden = new Set<string>()
    for (const a of sichtbar) if (gewaehlt.has(a.id)) gefunden.add(a.workspaceId)
    return [...gefunden]
  }, [sichtbar, gewaehlt])

  /**
   * **Was in der Pool-Auswahl steht.**
   *
   * | Auswahl | Angebot |
   * |---|---|
   * | noch keine | alle meine Vorräte, nach Welt gruppiert – man darf den Pool zuerst wählen |
   * | eine Welt | deren Vorräte |
   * | zwei Welten | keine, mit Hinweis – die Priorität geht trotzdem |
   */
  const poolsZurAuswahl =
    weltenDerAuswahl.length === 0
      ? pools
      : weltenDerAuswahl.length === 1
        ? poolsDerWelt(weltenDerAuswahl[0])
        : []

  /**
   * **Eine Wahl, die nicht mehr angeboten wird, gilt nicht mehr** – abgeleitet statt in einem
   * Effekt zurückgesetzt. Wer erst „Sofort (Entwicklung)" wählt und dann eine Marketing-Zeile
   * anhakt, soll nicht mit einem unsichtbaren Wert einordnen.
   */
  const poolGueltig = poolsZurAuswahl.some((p) => p.id === pool) ? pool : ''

  /** Nur bei mehreren Welten ist die Gruppierung eine Auskunft; sonst ist sie eine Zeile mehr. */
  const weltName = (id: string) => welten?.find((w) => w.id === id)?.label ?? id
  const poolsGruppiert =
    (welten?.length ?? 0) > 1 && new Set(poolsZurAuswahl.map((p) => p.workspaceId)).size > 1

  return (
    <>
      <Topbar
        titel="Eingang"
        untertitel={`${aufgaben.length} unverteilt · weder Pool noch Zeitfenster noch mögliche Entwickler`}
      />

      {/**
       * **Was der Eingang jetzt zeigt** – geändert am 18.08.2026. Vorher hing die Liste am
       * Status `eingang`, den GitLab und NetCon setzen. Jetzt zeigt sie, was **noch nicht
       * verteilt** ist, und das ist die Arbeit, die hier ansteht.
       */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[11.5px]">
        <span className="text-muted">Sortiert nach</span>
        {(
          [
            ['alter', SORTIER_LABEL.alter],
            ['neueste', SORTIER_LABEL.neueste],
            ['prioritaet', SORTIER_LABEL.prioritaet],
            ['vorschlaege', SORTIER_LABEL.vorschlaege],
          ] as const
        ).map(([wert, beschriftung]) => (
          <button
            key={wert}
            type="button"
            onClick={() => setSortierung(wert)}
            className={`cursor-pointer rounded-lg px-2.5 py-1 font-semibold ${
              sortierung === wert ? 'bg-ink text-white' : 'border border-line2 text-ink2'
            }`}
          >
            {beschriftung}
          </button>
        ))}
        <span className="text-muted">· die Wahl gilt für alle Zeilen</span>

        {/**
         * **Wo der Schalter stand** (E-285), steht seit dem 01.09.2026 eine Auskunft
         * ([E-296](../../../docs/ENTSCHEIDUNGEN.md#e-296)): der Eingang geht immer über alle
         * Arbeitswelten, in denen man Mitglied ist. Sie erscheint nur, wenn es mehr als eine
         * gibt – bei einer einzigen wäre sie eine Zeile über etwas, das niemand anders erwartet.
         */}
        {(welten?.length ?? 0) > 1 && (
          <span
            className="ml-auto text-muted"
            title="Der Eingang ist nicht an den Umschalter oben gebunden – er zeigt alles, wofür du berechtigt bist"
          >
            🌐 über alle deine Arbeitswelten
          </span>
        )}
      </div>

      {/**
       * **Anfragen stehen über dem Eingang, nicht darin** (Phase 9, Kap. 4.8).
       *
       * Sie sind ein eigener Kanal, aber keine Aufgaben – die häufigste gute Antwort auf
       * eine Frage lautet *„gibt es schon, ist Aufgabe #287"*, und dabei entsteht gerade
       * keine Arbeit. Stünden sie in derselben Liste, müsste man sie erst wieder
       * heraussortieren.
       */}
      <AnfragenListe role={rolle} />

      {/**
       * ── Kanäle (Mockup: Kopfleiste) ──────────────────────────
       *
       * **Die Karten sind der Filter.** Sie sahen schon immer aus wie Knöpfe; seit dem
       * 20.08.2026 sind sie welche. Ein Kanal ohne Zeilen bleibt stehen, aber tot – dass es
       * die Quelle gibt und gerade nichts von ihr liegt, ist selbst eine Auskunft.
       */}
      {/**
       * **Das Suchfeld steht über den Karten**, weil es vor ihnen greift – die Zahlen darunter
       * gehören zum Suchergebnis, nicht zum Gesamtbestand.
       */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={suche}
          onChange={(e) => waehleSuche(e.target.value)}
          placeholder="Titel, Ticketnummer oder Person …"
          aria-label="Eingang durchsuchen"
          className="w-[260px] rounded-[10px] border border-line2 bg-surface px-[13px] py-2 text-[13px] outline-none placeholder:text-muted"
        />
        {suche.trim() && (
          <>
            <span className="text-[12px] text-muted">
              {gesucht.length} von {aufgaben.length}
            </span>
            <button
              type="button"
              onClick={() => waehleSuche('')}
              className="cursor-pointer text-[12px] text-accent-deep underline"
            >
              zurücksetzen
            </button>
          </>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-2.5">
        <KanalKarte
          label="Alle Kanäle"
          zeile={suche.trim() ? `${gesucht.length} passen` : `${aufgaben.length} unverteilt`}
          stufe={<span className="text-muted">● ohne Filter</span>}
          aktiv={kanal === 'alle'}
          auf={() => waehleKanal('alle')}
        />
        {kanaele.map((k) => {
          const anzahl = proKanal.get(k.id) ?? 0
          const aktiv = kanal === k.id
          return (
            <KanalKarte
              key={k.id}
              label={k.label}
              zeile={`${anzahl > 0 ? `${anzahl} unverteilt` : 'nichts offen'} · ${k.regeln} ${
                k.regeln === 1 ? 'Regel' : 'Regeln'
              }`}
              stufe={
                <span className={k.stufe === 'automatisch' ? 'text-pool-sofort' : 'text-muted'}>
                  ● {STUFEN_LABELS[k.stufe]}
                </span>
              }
              aktiv={aktiv}
              /** Ein Kanal ohne Zeilen filtert auf nichts – der Klick wäre eine Sackgasse. */
              stumm={anzahl === 0 && !aktiv}
              /** Nochmal auf den gewählten Kanal heißt: Filter weg. Kein zweiter Knopf dafür. */
              auf={() => waehleKanal(aktiv ? 'alle' : k.id)}
            />
          )
        })}
      </div>

      {/**
       * **Die Zeile, die das Konzept verlangt** (Kap. 4.1): jeder Kanal startet manuell und
       * wird erst hochgestuft, wenn seine Regeln eine stabile Trefferquote haben. Sie steht
       * hier, damit niemand die Stufen für eine Einstellung hält, die man einfach umlegt.
       */}
      {bereiche.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5 text-[12px]">
          <span className="text-muted">Bereich:</span>
          <FilterKnopf aktiv={bereich === 'alle'} auf={() => setBereich('alle')}>
            alle ({nachKanal.length})
          </FilterKnopf>
          {bereiche.map((b) => (
            <FilterKnopf key={b.wert} aktiv={bereich === b.wert} auf={() => setBereich(b.wert)}>
              {b.label} ({b.anzahl})
            </FilterKnopf>
          ))}
        </div>
      )}

      <p className="mb-4 text-[11.5px] text-muted">
        Regeln laufen erst automatisch, wenn jemand sie hochstuft – bis dahin schlagen sie nur vor.
        Es gibt keinen Schalter, der alle auf einmal scharf stellt.
      </p>

      {/**
       * **Wie viele Vorschläge warten** – sichtbar, ohne zu scrollen.
       *
       * Seit dem 18.08.2026 der eigentliche Grund, warum sie nicht mehr in jeder Sortierung
       * nach oben gezogen werden müssen: bei 95 Zeilen ist eine Zahl im Kopf der bessere
       * Beleg dafür, dass es welche gibt, als eine Position in der Liste – und sie kostet
       * die Sortierung nichts.
       */}
      {/**
       * **Der Werkzeugkasten klappt zu** (Nils, 24.08.2026: *„die Gruppe mit dem Bereich
       * zwischen den Kanälen und der Liste zum Einklappen, sonst sieht man nicht so viel vom
       * Eingang"*).
       *
       * Vier Blöcke – Vorschlags-Zähler, „Auf einmal übernehmen", „Zuletzt gemacht",
       * Sammel-Einordnung – füllten den halben Bildschirm, bevor die erste Aufgabe kam. Es
       * sind Werkzeuge, keine Inhalte: man braucht sie, wenn man sie braucht.
       *
       * **Aufgeklappt ist der Standard**, nicht zugeklappt: wer sie nie gesehen hat, soll
       * nicht raten müssen, dass es sie gibt. Die Wahl wird gemerkt.
       */}
      <button
        type="button"
        onClick={() => setWerkzeugeOffen(werkzeugeOffen === 'auf' ? 'zu' : 'auf')}
        aria-expanded={werkzeugeOffen === 'auf'}
        className="mb-2 cursor-pointer text-[12px] text-muted hover:text-ink"
      >
        {werkzeugeOffen === 'auf' ? '▾' : '▸'} Werkzeuge
        {werkzeugeOffen === 'zu' && offeneVorschlaege > 0 && (
          <span className="ml-1.5 font-semibold text-accent-deep">
            · {offeneVorschlaege} KI-Vorschläge warten
          </span>
        )}
      </button>

      {werkzeugeOffen === 'auf' && offeneVorschlaege > 0 && (
        <p className="mb-2 text-[12.5px]">
          <strong className="text-accent-deep">{offeneVorschlaege} KI-Vorschläge</strong> warten auf
          eine Entscheidung
          {sortierung === 'vorschlaege' ? (
            ' – sie stehen oben in der Liste.'
          ) : (
            <>
              {' – '}
              <button
                type="button"
                onClick={() => setSortierung('vorschlaege')}
                className="cursor-pointer font-semibold text-accent-deep underline"
              >
                nach oben holen
              </button>
              .
            </>
          )}
        </p>
      )}

      {werkzeugeOffen === 'auf' && (
        <>
          <SammelUebernahme />
          <ZuletztGemacht />
        </>
      )}

      {/* ── Sammel-Einordnung ──────────────────────────────────── */}
      {werkzeugeOffen === 'auf' && sichtbar.length > 0 && (
        <Card className="mb-3">
          <CardPad className="flex flex-wrap items-center gap-2.5 text-[12.5px]">
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={alleGewaehlt}
                onChange={() =>
                  setGewaehlt(alleGewaehlt ? new Set() : new Set(sichtbar.map((a) => a.id)))
                }
              />
              alle
            </label>
            <strong className="tabular">{gewaehlt.size}</strong> gewählt
            <select
              className="rounded-md border border-line px-2 py-1 disabled:bg-[#f3f2ef] disabled:text-muted"
              value={poolGueltig}
              disabled={weltenDerAuswahl.length > 1}
              onChange={(e) => setPool(e.target.value)}
              aria-label="Pool"
            >
              <option value="">Pool wählen …</option>
              {poolsGruppiert
                ? [...new Set(poolsZurAuswahl.map((p) => p.workspaceId))].map((w) => (
                    <optgroup key={w} label={weltName(w)}>
                      {poolsZurAuswahl
                        .filter((p) => p.workspaceId === w)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.icon} {p.label}
                          </option>
                        ))}
                    </optgroup>
                  ))
                : poolsZurAuswahl.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.icon} {p.label}
                    </option>
                  ))}
            </select>
            {/**
             * **Zwei Welten in einer Auswahl** – dann gibt es keinen Pool, der für beide gilt
             * ([E-296](../../../docs/ENTSCHEIDUNGEN.md#e-296)). Die Priorität geht trotzdem: sie
             * ist eine Aussage über die Aufgabe, keine über eine Arbeitswelt.
             */}
            {weltenDerAuswahl.length > 1 && (
              <span className="text-[11px] text-muted">
                Pools gelten je Arbeitswelt – die Auswahl umfasst{' '}
                {weltenDerAuswahl.map(weltName).join(' und ')}. Priorität geht trotzdem.
              </span>
            )}
            <select
              className="rounded-md border border-line px-2 py-1"
              value={prio}
              onChange={(e) => setPrio(e.target.value as TaskPriority | '')}
              aria-label="Priorität"
            >
              <option value="">Priorität lassen</option>
              {TASK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  Priorität {p}
                </option>
              ))}
            </select>
            <Button
              disabled={
                gewaehlt.size === 0 || (poolGueltig === '' && prio === '') || einordnen.isPending
              }
              onClick={() =>
                einordnen.mutate({
                  taskIds: [...gewaehlt],
                  poolIds: poolGueltig ? [poolGueltig] : [],
                  ...(prio ? { priority: prio } : {}),
                })
              }
            >
              {gewaehlt.size > 1 ? `${gewaehlt.size} einordnen` : 'Einordnen'}
            </Button>
            {/**
             * **Der zweite Ausgang aus dem Eingang.** „Einordnen" heißt *das wird Arbeit*,
             * „Parken" heißt *das ist nur zur Kenntnis* – zwei Antworten auf dieselbe Frage,
             * also stehen sie nebeneinander. Er braucht weder Pool noch Priorität: wer parkt,
             * hat gerade entschieden, dass beides keine Rolle spielt.
             */}
            <Button
              variant="ghost"
              disabled={gewaehlt.size === 0 || parken.isPending}
              onClick={() => parken.mutate([...gewaehlt])}
              title="Legt die Aufgaben auf den Parkplatz und stellt sie zurück – sichtbar, aber nicht ziehbar"
            >
              {gewaehlt.size > 1 ? `${gewaehlt.size} parken` : 'Parken'}
            </Button>
            <Button
              variant="ghost"
              disabled={erzeugen.isPending}
              onClick={() => erzeugen.mutate({ limit: 25 })}
              title="Fragt das hinterlegte Modell nach einem Pool – kostet ein paar Zehntel-Cent"
            >
              {erzeugen.isPending ? 'Modell wird gefragt …' : 'KI-Vorschläge holen'}
            </Button>
            {erzeugen.data && (
              <span className="text-[11px] text-muted">
                {erzeugen.data.gespeichert} Vorschläge
                {erzeugen.data.centGeschaetzt !== null
                  ? ` · ${erzeugen.data.centGeschaetzt.toFixed(2)} US-Cent`
                  : ''}
                {erzeugen.data.fehler.length > 0 ? ` · ${erzeugen.data.fehler.length} Fehler` : ''}
              </span>
            )}
            {/**
             * **Der Hinweis gehört hierher, nicht in eine Fußnote.** „Einordnen" ändert den
             * Status *nicht*: es heißt „ich weiß jetzt, wohin das gehört", nicht „ich habe
             * angefangen". Wer das verwechselt, wundert sich, warum die Aufgabe im Eingang
             * bleibt.
             */}
            <span className="text-[11px] text-muted">
              Setzt Pool und Priorität. Der Status bleibt – gezogen wird aus dem Pool.
              {' Parken legt dagegen auf den Parkplatz: sichtbar, aber nicht ziehbar.'}
            </span>
            {parken.error && (
              <span role="alert" className="text-[11.5px] text-pool-sofort">
                {parken.error.message}
              </span>
            )}
          </CardPad>
        </Card>
      )}

      {/**
       * **Der Hinweis folgt der Wahl** (Nils, 24.08.2026: *„der ändert sich nie"*).
       *
       * Hier stand fest „älteste zuerst", unabhängig davon, wonach tatsächlich sortiert wurde –
       * ein Satz, der beim ersten Umschalten zur Falschaussage wird. Ein Hinweis, der lügt, ist
       * schlimmer als keiner: er erklärt eine Reihenfolge, die man gerade nicht sieht.
       */}
      <SectionTitle hint={SORTIER_HINWEIS[sortierung]}>Unsortiert</SectionTitle>

      {isLoading && <p className="text-[12.5px] text-muted">wird geladen …</p>}

      {!isLoading && sichtbar.length === 0 && (
        <Card>
          <CardPad className="text-[12.5px] text-muted">
            {/**
             * **Leer heißt nicht immer dasselbe.** „Alles hat einen Platz" nach einer Suche
             * ohne Treffer wäre eine Falschmeldung – und zwar eine beruhigende, also die
             * schlechtere Sorte.
             */}
            {suche.trim() ? (
              <>
                Keine Aufgabe im Eingang passt zu „{suche.trim()}“.{' '}
                <button
                  type="button"
                  onClick={() => waehleSuche('')}
                  className="cursor-pointer font-semibold text-accent-deep underline"
                >
                  Suche zurücksetzen
                </button>{' '}
                – oder in{' '}
                <Link to={ZU_ALLEN} className="font-semibold text-accent-deep underline">
                  Alle Aufgaben
                </Link>{' '}
                suchen, dort steht auch, was schon verteilt ist.
              </>
            ) : (
              'Nichts im Eingang. Alles, was hereinkam, hat einen Platz.'
            )}
          </CardPad>
        </Card>
      )}

      <div className="grid gap-2">
        {sichtbar.map((a) => (
          <Card key={a.id}>
            <CardPad className="flex gap-3">
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={gewaehlt.has(a.id)}
                onChange={() => umschalten(a.id)}
                aria-label={`${a.title} auswählen`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  {/**
                   * Der Titel ist der Knopf. **Kein zusätzliches „öffnen"-Symbol**: was
                   * anklickbar aussieht, soll anklickbar sein, und ein zweites Ziel neben
                   * dem Titel wäre eine Stelle mehr, die man verfehlen kann.
                   */}
                  <AufgabenVerweis
                    aufgabeId={a.id}
                    className="text-left text-[13.5px] font-bold hover:text-accent hover:underline"
                  >
                    {a.title}
                  </AufgabenVerweis>
                  {/**
                   * **Welche Arbeitswelt – immer** (01.09.2026,
                   * [E-286](../../../docs/ENTSCHEIDUNGEN.md#e-286)).
                   *
                   * Nils: *„Hier bitte immer kennzeichnen. sonst muss ich immer schauen in
                   * welchem workspace ich bin."*
                   *
                   * Sie stand zuerst unter `weltenGemischt` – die Regel aus „Mein Tag", wo eine
                   * Marke an jeder Zeile Rauschen wäre. Im **Eingang** ist die Rechnung eine
                   * andere: hier wird **einsortiert**, und die Frage *in welche Welt lege ich
                   * das* ist die Frage der Ansicht, nicht eine Nebenauskunft. Eine Liste ohne
                   * Marke zwingt zum Blick auf den Umschalter oben – und der beantwortet sie
                   * beim Modus „Alle Arbeitswelten" gar nicht mehr.
                   *
                   * Vor der Quelle, weil sie die gröbere Einordnung ist: erst *wo gehört das
                   * hin*, dann *woher kam es*.
                   */}
                  <AufgabenWeltMarke workspaceId={a.workspaceId} />
                  <SourceBadge source={a.sourceId as TaskSource} label={a.externalKey} />
                  {a.scopeLabel && (
                    <span className="rounded bg-[#f0efec] px-1.5 py-0.5 text-[10.5px] text-ink2">
                      {a.scopeLabel}
                    </span>
                  )}
                  {a.externalUrl && (
                    <a
                      href={a.externalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] text-muted hover:underline"
                    >
                      öffnen ↗
                    </a>
                  )}
                </div>

                <div className="mt-1 flex flex-wrap gap-x-4 text-[11.5px] text-muted">
                  {/**
                   * Datum immer, nur mit unterschiedlicher Herkunft: importierte Aufgaben
                   * tragen das Datum der Quelle, in Work angelegte ihr Anlagedatum. „ohne
                   * Datum" stand hier für genau die zweite Sorte – und war falsch.
                   */}
                  <span>
                    {a.sourceCreatedAt ? 'aus der Quelle vom ' : 'angelegt am '}
                    {entstanden(a).toLocaleDateString('de-DE')}
                  </span>
                  {/**
                   * **Was fehlt, steht da – nicht was da ist.** Bei 65 von 67 Aufgaben ohne
                   * Priorität wäre „Priorität: –" 65-mal dieselbe Nichtaussage; „keine
                   * Priorität" als Mangel benannt ist die Arbeitsanweisung.
                   */}
                  {!a.priority && <span>keine Priorität</span>}
                  {!a.assigneeName && <span>kein Bearbeiter</span>}
                  {/**
                   * **Der Mangel, der die anderen aussticht.** Ohne Priorität lässt sich eine
                   * Aufgabe einordnen; ohne Text nicht – da hilft nur nachfragen. Deshalb in
                   * Warnfarbe und nicht in der Reihe der übrigen.
                   */}
                  {a.rueckfrage && (
                    <span
                      className="font-semibold text-pool-sofort"
                      title="Kurzer Titel und so gut wie kein Text – so ist nicht zu entscheiden, wohin das gehört"
                    >
                      Rückfrage nötig
                    </span>
                  )}
                  {a.priority && <span>Priorität {a.priority}</span>}
                  {a.assigneeName && <span>{a.assigneeName}</span>}
                </div>

                {a.description && (
                  <p className="mt-1.5 line-clamp-2 text-[12px] text-ink2">
                    {lesbarerText(a.description).slice(0, 200)}
                  </p>
                )}

                {/**
                 * Der Regel-Vorschlag – heute die einzige Vorsortierung, die es gibt.
                 * Sichtbar zu machen, was eine Regel *sagen würde*, ist der Weg, sie zu
                 * beurteilen, bevor jemand sie hochstuft.
                 */}
                {/**
                 * **Der KI-Vorschlag** – der Platz, den das Mockup dafür vorsieht, jetzt
                 * gefüllt. Mit Begründung und Konfidenz, weil ein Vorschlag ohne beides
                 * nicht zu beurteilen ist (Kap. 4.1).
                 */}
                {a.vorschlagId && (
                  <Vorschlagszeile
                    aufgabe={a}
                    /** Die Vorräte **ihrer** Welt – E-296, siehe `poolsDerWelt`. */
                    pools={poolsDerWelt(a.workspaceId)}
                    beimBearbeiten={() => setBearbeite(a.id)}
                    beimUebernehmen={() =>
                      uebernehmen.mutate({
                        taskId: a.id,
                        werte: {
                          ...(a.vorschlagPool !== null ? { pool: a.vorschlagPool } : {}),
                          ...(a.vorschlagFenster !== null
                            ? { zeitfenster: a.vorschlagFenster }
                            : {}),
                          ...(a.vorschlagGroesse !== null ? { groesse: a.vorschlagGroesse } : {}),
                          ...(a.vorschlagFaellig !== null ? { faellig: a.vorschlagFaellig } : {}),
                          ...(a.vorschlagPersonen !== null
                            ? { personen: a.vorschlagPersonen }
                            : {}),
                        },
                      })
                    }
                    beimVerwerfen={() =>
                      uebernehmen.mutate({
                        taskId: a.id,
                        werte: {
                          ...(a.vorschlagId !== null ? { pool: null } : {}),
                          ...(a.vorschlagFensterId !== null ? { zeitfenster: null } : {}),
                          ...(a.vorschlagGroesseId !== null ? { groesse: null } : {}),
                          ...(a.vorschlagFaelligId !== null ? { faellig: null } : {}),
                          ...(a.vorschlagPersonenId !== null ? { personen: null } : {}),
                        },
                      })
                    }
                  />
                )}

                {a.regelVorschlag && (
                  <p className="mt-1.5 text-[11.5px] text-accent">
                    Regel „{a.regelVorschlag.regel}" schlägt vor:{' '}
                    {a.regelVorschlag.status ?? 'einordnen'}
                    {a.regelVorschlag.hinweis ? ` – ${a.regelVorschlag.hinweis}` : ''}
                  </p>
                )}

                {/**
                 * **Wer laut Regeln in Frage kommt** (18.08.2026, [E-128](../../../docs/ENTSCHEIDUNGEN.md)).
                 *
                 * Die Regelnamen stehen dabei, nicht nur die Personen: *„Chris"* ist ein
                 * Vorschlag, *„Chris – Regel Portierungen"* ist einer, den man beurteilen
                 * kann. Wer eine Regel für falsch hält, weiß damit sofort, welche er
                 * ändern muss.
                 */}
                {a.wunschVorschlag && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11.5px]">
                    <span className="text-accent">
                      ★ Kommen in Frage: <strong>{a.wunschVorschlag.namen.join(', ')}</strong>
                      <span className="text-muted">
                        {' '}
                        · {a.wunschVorschlag.regeln.length === 1 ? 'Regel' : 'Regeln'} „
                        {a.wunschVorschlag.regeln.join('", „')}"
                      </span>
                    </span>
                    <Button
                      size="sm"
                      disabled={wunschUebernehmen.isPending}
                      onClick={() =>
                        wunschUebernehmen.mutate({
                          id: a.id,
                          wunschPersonIds: a.wunschVorschlag!.personIds,
                        })
                      }
                      aria-label={`Vorschlag für ${a.title} übernehmen`}
                    >
                      übernehmen
                    </Button>
                  </div>
                )}
              </div>
            </CardPad>
          </Card>
        ))}
      </div>

      {/**
       * **Das Popup steht außerhalb der Liste**, nicht in der Karte: eine Karte, die sich
       * aufklappt, verschiebt alles darunter – und wer sich durch den Eingang arbeitet,
       * verliert dabei die Stelle, an der er war.
       */}
      {bearbeite !== null &&
        (() => {
          const a = sichtbar.find((z) => z.id === bearbeite)
          if (!a?.vorschlagId) return null
          return (
            <VorschlagBearbeiten
              aufgabe={a}
              /** Die Vorräte **ihrer** Welt – E-296, siehe `poolsDerWelt`. */
              pools={poolsDerWelt(a.workspaceId)}
              personen={personen}
              laeuft={uebernehmen.isPending}
              beimSchliessen={() => setBearbeite(null)}
              beimUebernehmen={(werte, felder) =>
                uebernehmen.mutate({ taskId: a.id, werte, felder })
              }
            />
          )
        })()}
    </>
  )
}

/**
 * **Ein Vorschlag, eine Zeile** (Nils, 20.08.2026).
 *
 * Vorher stand je Feld ein eigener Block mit eigenem *setzen* und *verwerfen* – bei drei
 * Feldern also sechs Knöpfe je Aufgabe, und der Eingang war eine Wand aus Entscheidungen.
 * Nils: *„Ich möchte nicht 4 Stück je Aufgabe haben."*
 *
 * Hier steht, **was das Modell insgesamt vorschlägt**, in Lesereihenfolge und ohne Knöpfe
 * dazwischen. Zwei Wege führen weiter: übernehmen (der Normalfall) oder bearbeiten (wenn ein
 * Wert nicht stimmt). Die Begründung steht darunter, weil ein Vorschlag ohne sie nicht zu
 * beurteilen ist (Kap. 4.1) – gekürzt, denn wer mehr wissen will, öffnet die Aufgabe.
 */
function Vorschlagszeile({
  aufgabe,
  pools,
  beimUebernehmen,
  beimBearbeiten,
  beimVerwerfen,
}: {
  aufgabe: {
    vorschlagPool: string | null
    vorschlagFenster: string | null
    vorschlagGroesse: string | null
    vorschlagFaellig: string | null
    vorschlagPersonenNamen: string | null
    vorschlagKonfidenz: number | null
    vorschlagBegruendung: string | null
    /** Steht einer da, ist der Personenvorschlag eine Ergänzung – siehe unten. */
    assigneeName: string | null
  }
  pools: { id: string; label: string; icon: string }[]
  beimUebernehmen: () => void
  beimBearbeiten: () => void
  beimVerwerfen: () => void
}) {
  const pool = pools.find((p) => p.id === aufgabe.vorschlagPool)
  /**
   * **Alles, was das Modell sagt, in einer Zeile.** Felder, zu denen es nichts sagen konnte,
   * fehlen einfach – „unklar" dreimal untereinander wäre eine Frage ohne Anlass.
   */
  const teile = [
    pool ? `${pool.icon} ${pool.label}` : aufgabe.vorschlagPool,
    aufgabe.vorschlagFenster
      ? (ZEITFENSTER_LABELS[aufgabe.vorschlagFenster as Zeitfenster] ?? aufgabe.vorschlagFenster)
      : null,
    aufgabe.vorschlagGroesse
      ? (GROESSE_LABELS[aufgabe.vorschlagGroesse as Aufgabengroesse] ?? aufgabe.vorschlagGroesse)
      : null,
    /**
     * **Die Fälligkeit trägt ihr Wort mit.** „30.09." allein sähe aus wie irgendein Datum;
     * „fällig 30.09." sagt, dass es eine Zusage ist und keine Schätzung.
     */
    aufgabe.vorschlagFaellig ? `fällig ${tagKompakt(aufgabe.vorschlagFaellig)}` : null,
    /**
     * **Mit Stern, wie überall sonst.** „Kommen in Frage" heißt an jeder anderen Stelle in Work
     * `★` – dieselbe Sache zweimal verschieden zu kennzeichnen ist die Art Kleinigkeit, an der
     * man merkt, dass zwei Leute daran gebaut haben.
     *
     * **Und mit „zusätzlich", wenn die Aufgabe schon einen Bearbeiter hat** (Nils, 20.08.2026,
     * [E-202](../docs/ENTSCHEIDUNGEN.md)): *„ein gesetzter bearbeiter hat vorrang."*
     * Übernehmen ergänzt „kommen in Frage" und rührt den Bearbeiter nicht an – aber ein
     * einzelner Stern-Name direkt unter der Zeile *„Zugewiesen an: Christian Hunhoff"* liest
     * sich wie ein Gegenvorschlag. Ein Wort entscheidet hier darüber, ob jemand den Knopf
     * drückt.
     */
    aufgabe.vorschlagPersonenNamen
      ? `★ ${aufgabe.assigneeName ? 'zusätzlich ' : ''}${aufgabe.vorschlagPersonenNamen}`
      : null,
  ].filter(Boolean)

  return (
    <div className="mt-2 rounded-md border border-[#bcd6f5] bg-accent-soft px-2.5 py-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted">KI</span>
        <strong className="text-[12.5px] text-accent-deep">
          {teile.length > 0 ? teile.join(' · ') : 'unklar'}
        </strong>
        {aufgabe.vorschlagKonfidenz !== null && (
          <span
            className="text-[11px] text-muted"
            title="Selbsteinschätzung des Modells – noch nicht durch die Übernahmequote bestätigt"
          >
            {aufgabe.vorschlagKonfidenz} %
          </span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          {teile.length > 0 && <Button onClick={beimUebernehmen}>Übernehmen</Button>}
          <Button variant="ghost" onClick={beimBearbeiten}>
            bearbeiten …
          </Button>
          <Button variant="ghost" onClick={beimVerwerfen}>
            verwerfen
          </Button>
        </span>
      </div>
      {aufgabe.vorschlagBegruendung && (
        <p className="mt-0.5 text-[11.5px] text-ink2">{aufgabe.vorschlagBegruendung}</p>
      )}
    </div>
  )
}

/**
 * **Das Bearbeiten-Popup** – Nils: *„sie ggf. zu bearbeiten (Popup), dann zu übernehmen (mit
 * ggf. ein-zwei geänderten Parametern)"*.
 *
 * ## Warum die Felder vorbelegt sind und nicht leer
 *
 * Weil der Vorschlag der Ausgangspunkt ist, nicht ein Formular. Wer nichts anfasst und Enter
 * drückt, übernimmt genau das, was in der Zeile stand – das ist der häufige Fall und darf
 * keinen einzigen Klick kosten.
 *
 * ## Warum ein Feld auch „nicht setzen" sein kann
 *
 * Die leere Auswahl heißt **verwerfen**, nicht „unverändert lassen": wer im Popup steht, hat
 * über das Feld nachgedacht, und ein Vorschlag, den jemand angesehen und weggelassen hat, ist
 * beurteilt. Nur Felder, zu denen es gar keinen Vorschlag gibt, tauchen hier nicht auf und
 * bleiben deshalb unangetastet.
 *
 * ## Warum hier **jedes** Feld steht, das die KI vorschlägt
 *
 * Nils, 20.08.2026: *„Hier muss noch mit rein: alle Daten, die durch die KI gesetzt /
 * vorgeschlagen werden."* Bis dahin fehlte „Kommen in Frage" – der Vorschlag stand in der Zeile
 * darüber und war im Popup nicht zu korrigieren. Wer ihn ändern wollte, musste das Popup
 * schließen, die Aufgabe öffnen und dort suchen; wer stattdessen einfach *Übernehmen* drückte,
 * trug drei Namen ein, die er nicht geprüft hatte. Ein Popup, das nur einen Teil der Aussagen
 * zeigt, macht aus „bearbeiten" ein Vertrauen.
 *
 * ## Seit dem 24.08.2026: **jedes** Feld, auch ohne Vorschlag
 *
 * Nils: *„Bei den KI-Vorschlägen – da müssen in dem Popup auch die nicht vorgeschlagenen Dinge
 * zur Auswahl stehen wie: Zeitraum, Wunsch-Entwickler, Bearbeiter, (nicht nur Pool)."*
 * ([E-219](../../../docs/ENTSCHEIDUNGEN.md#e-219))
 *
 * Vorher entschied das Schweigen des Modells darüber, was ein Mensch hier tun durfte: sagte es
 * zum Zeitraum nichts, gab es kein Feld – und der Eingang, der „schnell durchgearbeitet" werden
 * sollte, zwang zum Umweg über das Aufgabendetail. Das ist rückwärts: die Maske gehört dem
 * Menschen, nicht dem Modell.
 *
 * **Jedes Feld zeigt jetzt den Ist-Stand**, wenn kein Vorschlag dazu offen ist. Ein leeres
 * „Zeitraum" an einer Aufgabe, die auf „diese Woche" steht, wäre keine Auswahl, sondern eine
 * Falle – wer daneben den Pool korrigiert und speichert, hätte den Zeitraum unbemerkt gelöscht.
 *
 * **Der Bearbeiter kam mit dazu** – gegen [E-157](../../../docs/ENTSCHEIDUNGEN.md), aber das
 * betraf nur die *KI*: sie schlägt keinen Bearbeiter vor, und das bleibt so. Ein Mensch darf
 * ihn hier setzen. Weil eine vergebene Aufgabe in keinem Vorrat mehr liegt (`tasks.zuweisen`),
 * sperrt ein gewählter Bearbeiter die Poolwahl **sichtbar** – Nils' Entscheidung: *„Bearbeiter
 * gewinnt, Pool fällt weg."* Stumm zu schlucken, was jemand gerade eingestellt hat, wäre die
 * schlechtere Hälfte.
 */
function VorschlagBearbeiten({
  aufgabe,
  pools,
  personen,
  laeuft,
  beimSchliessen,
  beimUebernehmen,
}: {
  aufgabe: {
    title: string
    /** Die Kennung des **Pool**-Vorschlags – die Zeile, an der alle übrigen hängen. */
    vorschlagId: string | null
    vorschlagPool: string | null
    vorschlagFenster: string | null
    vorschlagGroesse: string | null
    vorschlagFaellig: string | null
    vorschlagPersonen: string | null
    vorschlagFensterId: string | null
    vorschlagGroesseId: string | null
    vorschlagFaelligId: string | null
    vorschlagPersonenId: string | null
    vorschlagBegruendung: string | null
    vorschlagKonfidenz: number | null
    /** Was ohne Vorschlag gilt – die Vorbelegung der Felder, zu denen das Modell schwieg. */
    istZeitfenster: string | null
    istGroesse: string | null
    istFaellig: string | null
    istBearbeiterId: string | null
    istPersonen: string | null
  }
  pools: { id: string; label: string; icon: string }[]
  /**
   * `role` als `PersonRole` und nicht als `string`: `zaehltAlsEntwickler` liest beide Felder,
   * und mit einem freien `string` wäre hier ein Tippfehler in einer Rollenbezeichnung kein
   * Fehler, sondern eine Liste, die stillschweigend leer bleibt.
   */
  personen: {
    id: string
    displayName: string
    isActive: boolean
    role: PersonRole
    arbeitetMit: boolean
  }[]
  laeuft: boolean
  beimSchliessen: () => void
  beimUebernehmen: (
    werte: Record<string, string | null>,
    felder: Record<string, string | null>,
  ) => void
}) {
  /**
   * **Vorschlag schlägt Ist-Stand** – aber nur, wenn es einen gibt. Sonst zeigt das Feld, was
   * gerade an der Aufgabe steht; das ist der Unterschied zwischen einer Auswahl und einer
   * Falle (siehe Kopf dieser Komponente).
   */
  const [pool, setPool] = useState(aufgabe.vorschlagPool ?? '')
  const [fenster, setFenster] = useState(aufgabe.vorschlagFenster ?? aufgabe.istZeitfenster ?? '')
  const [groesse, setGroesse] = useState(aufgabe.vorschlagGroesse ?? aufgabe.istGroesse ?? '')
  const [faellig, setFaellig] = useState(aufgabe.vorschlagFaellig ?? aufgabe.istFaellig ?? '')
  const [bearbeiter, setBearbeiter] = useState(aufgabe.istBearbeiterId ?? '')
  /**
   * **„Kommen in Frage" ist eine Menge, kein Wert** – deshalb Marken zum An- und Abwählen und
   * kein Auswahlfeld. Dieselbe Bedienung wie im Aufgabendetail: eine Mehrfachauswahl in einem
   * `select` ist die unbedienbarste Form dieser Frage.
   */
  const [wunsch, setWunsch] = useState<Set<string>>(
    () =>
      new Set((aufgabe.vorschlagPersonen ?? aufgabe.istPersonen ?? '').split(',').filter(Boolean)),
  )

  /**
   * **Ein Bearbeiter nimmt die Aufgabe aus dem Vorrat.** Solange einer gewählt ist, wäre eine
   * Poolwahl daneben eine Angabe, die der Server im selben Zug wieder abräumt – also wird sie
   * gesperrt und der Grund danebengeschrieben, statt sie stumm verfallen zu lassen.
   */
  const poolGesperrt = bearbeiter !== ''

  /**
   * **Zur Wahl stehen die Entwickler:innen** – wie im Aufgabendetail.
   *
   * **Wen das Modell vorgeschlagen hat, steht auf jeden Fall dabei**, auch wenn die
   * Personenliste ihn nicht führt (ausgeschieden, andere Rolle). Sonst ginge eine Kennung, die
   * niemand sieht, beim Übernehmen still mit – und das ist die schlimmere Hälfte: ein
   * unsichtbarer Vorschlag lässt sich nicht abwählen.
   */
  const auswahl = [
    ...personen.filter((p) => p.isActive && zaehltAlsEntwickler(p)),
    ...[...wunsch]
      .filter((id) => !personen.some((p) => p.isActive && zaehltAlsEntwickler(p) && p.id === id))
      .map((id) => ({
        id,
        displayName: personen.find((p) => p.id === id)?.displayName ?? 'unbekannte Person',
      })),
  ]

  /**
   * **Zwei Stapel, nicht einer** ([E-219](../../../docs/ENTSCHEIDUNGEN.md#e-219)).
   *
   * `werte` beantwortet *„was wird aus diesem Vorschlag?"* – dort heißt `null` **verwerfen**
   * und lässt den Bestand in Ruhe. `felder` beantwortet *„was soll an der Aufgabe stehen?"* –
   * dort heißt `null` **leeren**. In einen Stapel gefaltet wären „Zeitraum löschen" und
   * „Zeitraum-Vorschlag ablehnen" dieselbe Nachricht.
   *
   * Nur der Client kann das auseinanderhalten: **er** weiß, zu welchem Feld ein Vorschlag
   * offen steht. Deshalb sortiert er hier und nicht der Server.
   */
  const absenden = () => {
    const werte: Record<string, string | null> = {}
    const felder: Record<string, string | null> = {}

    /** Ein Vorschlag wird entschieden; ohne Vorschlag wird gesetzt – aber nur, was sich geändert hat. */
    const einsortieren = (
      feld: string,
      vorschlagId: string | null,
      wert: string,
      ist: string | null,
    ) => {
      if (vorschlagId !== null) werte[feld] = wert || null
      else if ((wert || null) !== (ist || null)) felder[feld] = wert || null
    }

    /**
     * **Pool ist der Sonderfall.** Er hat immer eine Vorschlagszeile (`vorschlagId` ist die des
     * Pool-Vorschlags), und bei gesperrter Auswahl gilt er als verworfen: der Bearbeiter hat
     * gewonnen, ein Pool wäre gleich wieder weg.
     */
    if (aufgabe.vorschlagId !== null) werte.pool = poolGesperrt ? null : pool || null
    else if (!poolGesperrt && pool) felder.pool = pool

    einsortieren('zeitfenster', aufgabe.vorschlagFensterId, fenster, aufgabe.istZeitfenster)
    einsortieren('groesse', aufgabe.vorschlagGroesseId, groesse, aufgabe.istGroesse)
    einsortieren('faellig', aufgabe.vorschlagFaelligId, faellig, aufgabe.istFaellig)

    /**
     * „Kommen in Frage" ist eine Menge – verglichen wird deshalb der sortierte Inhalt und
     * nicht die Zeichenkette, sonst gälte eine andere Reihenfolge als Änderung.
     */
    const gewaehlt = [...wunsch].sort().join(',')
    const bisher = (aufgabe.istPersonen ?? '').split(',').filter(Boolean).sort().join(',')
    if (aufgabe.vorschlagPersonenId !== null) {
      werte.personen = wunsch.size > 0 ? [...wunsch].join(',') : null
    } else if (gewaehlt !== bisher) {
      felder.personen = gewaehlt || null
    }

    /** Den Bearbeiter schlägt die KI nie vor – er geht immer den direkten Weg. */
    if ((bearbeiter || null) !== (aufgabe.istBearbeiterId ?? null)) {
      felder.bearbeiter = bearbeiter || null
    }

    beimUebernehmen(werte, felder)
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-black/30 p-3 pt-[4vh] sm:p-6 sm:pt-[12vh]"
      onClick={beimSchliessen}
      /**
       * **Enter übernimmt, Esc schließt** (Leitplanke 3: alles per Tastatur). Der Eingang wird
       * mit einer Hand durchgearbeitet – wer für jede Aufgabe zur Maus greifen muss, arbeitet
       * ihn nicht durch.
       */
      onKeyDown={(e) => {
        if (e.key === 'Escape') beimSchliessen()
        /**
         * **Nicht, wenn ein Knopf den Fokus hat.** Enter auf einem Knopf löst ihn selbst aus –
         * seit „Kommen in Frage" sind das Umschaltmarken, und ohne diese Zeile würde ein
         * Tastendruck die Person an- und die Aufgabe gleichzeitig durchwählen.
         */
        if (e.key === 'Enter' && (e.target as HTMLElement).closest('button')) return
        if (e.key === 'Enter' && !e.shiftKey && !laeuft) absenden()
      }}
      role="presentation"
    >
      <div
        className="w-full min-w-0 max-w-[520px] rounded-xl border border-line bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Vorschlag bearbeiten: ${aufgabe.title}`}
      >
        <div className="border-b border-line px-4 py-2.5">
          <p className="text-[13.5px] font-bold">{aufgabe.title}</p>
          {aufgabe.vorschlagBegruendung && (
            <p className="mt-0.5 text-[11.5px] text-ink2">
              {aufgabe.vorschlagBegruendung}
              {aufgabe.vorschlagKonfidenz !== null && (
                <span className="text-muted"> · {aufgabe.vorschlagKonfidenz} % sicher</span>
              )}
            </p>
          )}
        </div>

        <div className="grid gap-2.5 px-4 py-3 text-[12.5px]">
          <label className="flex items-center gap-2">
            <span className="w-[92px] shrink-0 font-semibold text-ink2">Pool</span>
            {/** Der Fokus landet auf dem ersten Feld, nicht auf dem Rahmen – sonst tut Enter nichts. */}
            <select
              autoFocus
              disabled={poolGesperrt}
              className="flex-1 rounded-md border border-line px-2 py-1 disabled:bg-[#f3f2ef] disabled:text-muted"
              value={poolGesperrt ? '' : pool}
              onChange={(e) => setPool(e.target.value)}
            >
              <option value="">— nicht setzen</option>
              {pools.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.icon} {p.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2">
            <span className="w-[92px] shrink-0 font-semibold text-ink2">Zeitraum</span>
            <select
              className="flex-1 rounded-md border border-line px-2 py-1"
              value={fenster}
              onChange={(e) => setFenster(e.target.value)}
            >
              <option value="">— nicht setzen</option>
              {ZEITFENSTER.map((z) => (
                <option key={z} value={z}>
                  {ZEITFENSTER_LABELS[z]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2">
            <span className="w-[92px] shrink-0 font-semibold text-ink2">Umfang</span>
            <select
              className="flex-1 rounded-md border border-line px-2 py-1"
              value={groesse}
              onChange={(e) => setGroesse(e.target.value)}
            >
              <option value="">— nicht setzen</option>
              {AUFGABEN_GROESSEN.map((g) => (
                <option key={g} value={g}>
                  {GROESSE_LABELS[g]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2">
            <span className="w-[92px] shrink-0 font-semibold text-ink2">Fälligkeit</span>
            {/**
             * **Ein Datumsfeld, kein Text.** Die Fälligkeit ist das einzige Feld hier, das
             * aus dem Text *zitiert* statt eingeschätzt wird – wer sie korrigiert, korrigiert
             * ein Datum und soll dafür einen Kalender bekommen.
             */}
            <input
              type="date"
              className="flex-1 rounded-md border border-line px-2 py-1"
              value={faellig}
              onChange={(e) => setFaellig(e.target.value)}
            />
          </label>

          {/**
           * **„Kommen in Frage"** (20.08.2026) – die fünfte Aussage des Modells, und bis eben
           * die einzige, die hier fehlte.
           *
           * `items-start` statt `items-center`: die Marken brechen bei sieben Entwicklern in
           * zwei Zeilen um, und eine mittig gesetzte Beschriftung stünde dann zwischen ihnen.
           *
           * **Additiv, nie ersetzend** ([E-128](../../../docs/ENTSCHEIDUNGEN.md)): wer schon in
           * Frage kommt, bleibt drin – abwählen heißt hier „diesen Vorschlag nicht übernehmen",
           * nicht „diese Person entfernen". Deshalb steht unter der Auswahl der Satz, der das
           * sagt; ohne ihn liest sich eine leere Reihe wie ein Löschen.
           */}
          {
            <div className="flex items-start gap-2">
              <span className="mt-1 w-[92px] shrink-0 font-semibold text-ink2">
                Kommen in Frage
              </span>
              <div className="flex flex-1 flex-wrap gap-1">
                {auswahl.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={wunsch.has(p.id)}
                    onClick={() =>
                      setWunsch((alt) => {
                        const neu = new Set(alt)
                        if (neu.has(p.id)) neu.delete(p.id)
                        else neu.add(p.id)
                        return neu
                      })
                    }
                    className={`cursor-pointer rounded-md px-2 py-px text-[11.5px] ${
                      wunsch.has(p.id)
                        ? 'bg-accent text-white'
                        : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                    }`}
                  >
                    {p.displayName}
                  </button>
                ))}
                {auswahl.length === 0 && (
                  <span className="text-[11.5px] text-muted">
                    Niemand ist als Entwickler:in gekennzeichnet – Rollen stehen in der
                    Benutzerverwaltung.
                  </span>
                )}
                <span className="basis-full text-[11px] text-muted">
                  {wunsch.size === 0
                    ? aufgabe.vorschlagPersonenId !== null
                      ? 'Niemand gewählt – der Vorschlag gilt als verworfen.'
                      : 'Niemand gewählt.'
                    : 'Wird ergänzt, nicht ersetzt – wer schon in Frage kommt, bleibt drin.'}
                </span>
              </div>
            </div>
          }

          {/**
           * **Der Bearbeiter** (24.08.2026, [E-219](../../../docs/ENTSCHEIDUNGEN.md#e-219)).
           *
           * Die KI schlägt ihn nie vor ([E-157](../../../docs/ENTSCHEIDUNGEN.md)) – hier
           * entscheidet also immer ein Mensch, und das Feld zeigt schlicht, was gilt.
           *
           * Er steht **unter** „Kommen in Frage", weil das die schwächere Aussage ist und die
           * stärkere zuletzt kommen soll: wer schon weiß, wer es macht, braucht die Vorauswahl
           * nicht mehr.
           */}
          <label className="flex items-center gap-2">
            <span className="w-[92px] shrink-0 font-semibold text-ink2">Bearbeiter</span>
            <select
              className="flex-1 rounded-md border border-line px-2 py-1"
              value={bearbeiter}
              onChange={(e) => setBearbeiter(e.target.value)}
            >
              <option value="">— niemand</option>
              {personen
                .filter((p) => p.isActive && zaehltAlsEntwickler(p))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
            </select>
          </label>

          {/**
           * **Der Satz, der die gesperrte Poolwahl erklärt.** Ohne ihn sähe ein ausgegrautes
           * Feld nach einem Fehler aus – dieselbe Lehre wie beim gesperrten Ziehen-Knopf im
           * „Mein Tag" (Commit `c656886`): ein Bedienelement, das schweigend nicht reagiert,
           * wirkt kaputt.
           */}
          {poolGesperrt && (
            <p className="text-[11px] text-pool-sofort">
              Mit Bearbeiter verlässt die Aufgabe den Pool – Work steuert über Ziehen, und was
              vergeben ist, liegt in keinem Vorrat mehr.
            </p>
          )}

          {/**
           * **Der Zeitraum läuft ab dem Übernehmen** – nicht ab dem Eingang und nicht ab dem
           * Erzeugen des Vorschlags ([E-156](../../../docs/ENTSCHEIDUNGEN.md)). Das steht hier,
           * weil es der Moment ist, in dem die Frist beginnt, und niemand sonst es sähe.
           */}
          {fenster !== '' && fenster !== (aufgabe.istZeitfenster ?? '') && (
            <p className="text-[11px] text-muted">
              Der Zeitraum wird ab heute gerechnet – ab dem Moment, in dem du übernimmst.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-line px-4 py-2.5">
          <Button disabled={laeuft} onClick={absenden}>
            Übernehmen
          </Button>
          <Button variant="ghost" disabled={laeuft} onClick={beimSchliessen}>
            Abbrechen
          </Button>
          <span className="ml-auto text-[11px] text-muted">Enter übernimmt · Esc schließt</span>
        </div>
      </div>
    </div>
  )
}

const STUFEN_LABELS: Record<string, string> = {
  manuell: 'Manuell',
  bestaetigung: 'Halbautomatisch',
  automatisch: 'Automatisch',
}

/**
 * **Eine Kanalkarte** – Auskunft und Filter in einem.
 *
 * Sie behält die Optik des Mockups (`v-eingang`, Kopfleiste); dazugekommen ist nur, dass ein
 * Klick den Eingang auf diesen Kanal einschränkt. Der gewählte Kanal ist am Rahmen zu
 * erkennen, nicht an einer Häkchen-Marke: die Karte trägt schon drei Zeilen, eine vierte
 * Auszeichnung darin würde sie zum Formular machen.
 */
function KanalKarte({
  label,
  zeile,
  stufe,
  aktiv,
  stumm = false,
  auf,
}: {
  label: string
  zeile: string
  stufe: React.ReactNode
  aktiv: boolean
  stumm?: boolean
  auf: () => void
}) {
  return (
    <button
      type="button"
      onClick={auf}
      disabled={stumm}
      aria-pressed={aktiv}
      className={`rounded-xl border px-[15px] py-2.5 text-left text-xs shadow-card ${
        aktiv
          ? 'border-accent bg-accent-soft'
          : stumm
            ? 'border-line bg-surface opacity-55'
            : 'cursor-pointer border-line bg-surface hover:border-accent'
      }`}
    >
      <b className="block text-[12.5px]">{label}</b>
      <small className="text-muted">{zeile}</small>
      <div className="mt-1 text-[11px]">{stufe}</div>
    </button>
  )
}

/**
 * Ein Filter-Knopf. Bewusst Knöpfe und keine Auswahlliste: bei fünf Bereichen sieht man so
 * **die Verteilung mit** – dass 38 von 67 Aufgaben aus `NetConWeb` kommen, ist selbst eine
 * Auskunft, und in einem zugeklappten Menü stünde sie nicht.
 */
function FilterKnopf({
  aktiv,
  auf,
  children,
}: {
  aktiv: boolean
  auf: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={auf}
      aria-pressed={aktiv}
      className={`rounded-full border px-2.5 py-1 text-[11.5px] ${
        aktiv
          ? 'border-accent bg-accent-soft text-accent-deep'
          : 'border-line text-ink2 hover:bg-[#fafaf8]'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * **Vorschläge stapelweise übernehmen – ab einer Konfidenz** (Phase 6, Abnahmekriterium
 * „Sammel-Bestätigung mehrerer Vorschläge funktioniert").
 *
 * ## Warum eine Schwelle und kein „alle übernehmen"
 *
 * Weil „alle" die Frage nicht beantwortet, die man vor dem Klicken hat: *worauf lasse ich
 * mich ein?* Eine Schwelle macht daraus eine Aussage – **„alles, wo das Modell sich sicher
 * ist"**. Und sie ist der Weg, auf dem Vertrauen wächst: erst bei 95 % bestätigen, die
 * Übernahmequote ansehen, dann 90 %.
 *
 * ## Warum die Zahl am Knopf steht
 *
 * Dieselbe Überlegung wie beim Probelauf der Triage-Regeln: **wer einen Stapel bestätigt,
 * muss vorher wissen, wie groß er ist.** „Übernehmen" allein ist eine Frage ins Blaue.
 *
 * Die Schwellen sind fest (95 · 90 · 80 · alle) und kein Schieberegler: bei einer
 * Selbsteinschätzung eines Modells ist der Unterschied zwischen 87 und 88 keiner, den
 * jemand begründen könnte.
 */
function SammelUebernahme() {
  const queryClient = useQueryClient()
  const { data: stufen = [] } = useQuery(vorschlagsStufenQuery)
  const [bericht, setBericht] = useState<string | null>(null)

  const uebernehmen = useMutation({
    mutationFn: (abKonfidenz: number) =>
      trpc.eingang.vorschlaegeSammelUebernehmen.mutate({ abKonfidenz }),
    onSuccess: async (b) => {
      setBericht(
        b.uebernommen === 0
          ? 'Nichts übernommen.'
          : `${b.uebernommen} übernommen${b.uebersprungenUnklar > 0 ? `, ${b.uebersprungenUnklar} übersprungen („unklar")` : ''}.`,
      )
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['pools'] })
      await queryClient.invalidateQueries({ queryKey: ['ki'] })
    },
  })

  /** Ohne offene Vorschläge wäre das eine Reihe Knöpfe, die alle nichts tun. */
  const hoechste = stufen.find((s) => s.anzahl > 0)
  if (!hoechste) return null

  return (
    <Card className="mb-3">
      <CardPad className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <span className="text-muted">Auf einmal übernehmen:</span>
        {stufen
          .filter((s) => s.anzahl > 0)
          .map((s) => (
            <Button
              key={s.abKonfidenz}
              variant={s.abKonfidenz === 95 ? 'primary' : 'ghost'}
              disabled={uebernehmen.isPending}
              onClick={() => {
                setBericht(null)
                uebernehmen.mutate(s.abKonfidenz)
              }}
              title={
                s.abKonfidenz === 0
                  ? 'Alle offenen Vorschläge mit Pool – auch die unsicheren'
                  : `Alle Vorschläge, bei denen das Modell mindestens ${s.abKonfidenz} % angibt`
              }
            >
              {s.abKonfidenz === 0 ? `alle (${s.anzahl})` : `ab ${s.abKonfidenz} % (${s.anzahl})`}
            </Button>
          ))}
        {bericht && <span className="text-ink2">{bericht}</span>}
        <span className="ml-auto text-[11px] text-muted">
          „Unklar" bleibt liegen – da gibt es nichts zu übernehmen.
        </span>
      </CardPad>
    </Card>
  )
}

/**
 * **Zuletzt gemacht – mit Rückgängig** (Phase 7).
 *
 * Die Phasendatei sagt, warum es das gibt: *„Die Rückgängig-Möglichkeit ist kein Komfort,
 * sondern die Voraussetzung dafür, dass sich jemand traut, eine Sammel-Bestätigung zu
 * drücken."*
 *
 * ## Warum zurückgenommene Aktionen stehen bleiben
 *
 * Weil das die interessantere Auskunft von beiden ist: dass jemand eine Sammelaktion
 * zurückgenommen hat, heißt, dass die Einordnung daneben lag. Sie verschwinden zu lassen wäre
 * eine Statistik, die nur die guten Fälle behält.
 */
/** Wie viele Titel je Zeile stehen, bevor gezählt wird. Vier passen in eine Zeile. */
const TITEL_JE_AKTION = 4

function ZuletztGemacht() {
  const queryClient = useQueryClient()
  const { data: aktionen = [] } = useQuery(massenaktionenQuery)
  const [fehler, setFehler] = useState<string | null>(null)

  const zurueck = useMutation({
    mutationFn: (id: string) => trpc.eingang.massenaktionZurueck.mutate({ id }),
    onSuccess: async (b) => {
      setFehler(b.ok ? null : b.grund)
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['pools'] })
      await queryClient.invalidateQueries({ queryKey: ['ki'] })
    },
  })

  if (aktionen.length === 0) return null

  return (
    <Card className="mb-3">
      <CardPad className="text-[12px]">
        <div className="mb-1 text-[11px] font-semibold tracking-[0.4px] text-muted uppercase">
          Zuletzt gemacht
        </div>
        {aktionen.map((a) => (
          <div key={a.id} className="flex flex-wrap items-baseline gap-2 py-1">
            <span className={a.zurueckgenommenAm ? 'text-muted line-through' : ''}>
              {a.beschreibung}
            </span>
            {/**
             * **Welche Aufgaben es traf** (Nils, 24.08.2026: *„man sieht gar nicht, was da
             * gemacht wurde"*). Ohne die Titel ist „rückgängig" ein Angebot, das man nicht
             * beurteilen kann. Anklickbar, weil die nächste Frage immer *„was steht da drin?"*
             * lautet – und die beantwortet nur das Detail.
             */}
            {a.aufgaben.slice(0, TITEL_JE_AKTION).map((t) => (
              <AufgabenVerweis
                key={t.id}
                aufgabeId={t.id}
                className="max-w-[320px] cursor-pointer truncate text-left text-accent-deep hover:underline"
                title={t.title}
              >
                {t.title}
              </AufgabenVerweis>
            ))}
            {a.aufgaben.length > TITEL_JE_AKTION && (
              <span className="text-[11px] text-muted">
                + {a.aufgaben.length - TITEL_JE_AKTION} weitere
              </span>
            )}
            <span className="text-[11px] text-muted">
              {new Date(a.ausgefuehrtAm).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {a.zurueckgenommenAm ? (
              <span className="text-[11px] text-muted">zurückgenommen</span>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled={zurueck.isPending}
                onClick={() => {
                  setFehler(null)
                  zurueck.mutate(a.id)
                }}
              >
                rückgängig
              </Button>
            )}
          </div>
        ))}
        {fehler && <p className="mt-1 text-[11.5px] text-pool-sofort">{fehler}</p>}
        <p className="mt-1.5 text-[11px] text-muted">
          Nur von heute. Zurückgenommen wird nur, was die Aktion selbst angefasst hat – wer
          inzwischen etwas anderes geändert hat, behält es.
        </p>
      </CardPad>
    </Card>
  )
}
